/**
 * Whether the first meeting has happened, and whether it is running right
 * now. Same split as `tour/store.ts`, for the same reason: "running" is a
 * property of this page load and lives in memory, "has this account met her
 * yet" is a property of the account on this device and lives in Dexie.
 *
 * Unlike the tour, there is no version to bump — this is a one-shot life
 * event, not a walkthrough that gains steps. A timestamp rather than a
 * boolean costs nothing and answers "since when" for free if that is ever
 * useful (a trophy, a Wrapped slide).
 */

import { create } from 'zustand'
import { getMeta, setMeta } from '../db/dexie.js'

export const GLITIME_META_KEY = 'glitime.metAt'

export async function glitimeMet(): Promise<boolean> {
  const at = await getMeta<unknown>(GLITIME_META_KEY, null)
  return typeof at === 'string'
}

export async function markGlitimeMet(): Promise<void> {
  await setMeta(GLITIME_META_KEY, new Date().toISOString())
}

/**
 * When, exactly — which the comment above guessed would be useful one day and
 * now is, twice over: the anniversary of the meeting is one of the five days
 * the whole app changes colour, and the dossier fills in a redacted field for
 * every stretch of time this account has known her.
 */
export async function glitimeMetAt(): Promise<string | null> {
  const at = await getMeta<unknown>(GLITIME_META_KEY, null)
  return typeof at === 'string' ? at : null
}

interface FirstMeetingState {
  open: boolean
}

export const useFirstMeetingState = create<FirstMeetingState>(() => ({ open: false }))

export function startFirstMeeting(): void {
  useFirstMeetingState.setState({ open: true })
}

export function endFirstMeeting(): void {
  useFirstMeetingState.setState({ open: false })
}
